import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getComponentMetadata, ComponentMetadata, MetadataHistoryEntry } from '../../data/mockData';
import { Calendar, Clock, User, FileText, ChevronRight, History, Zap, Hash, Building, Package, Wrench, CheckCircle, AlertTriangle, XCircle, Pause } from 'lucide-react';

interface TimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  field: string;
  history: MetadataHistoryEntry[];
  currentValue: string | number | Date;
}

const TimelineModal: React.FC<TimelineModalProps> = ({ isOpen, onClose, field, history, currentValue }) => {
  if (!isOpen) return null;

  const formatValue = (value: string | number | Date): string => {
    if (value instanceof Date) {
      return value.toLocaleDateString() + ' ' + value.toLocaleTimeString();
    }
    return String(value);
  };

  const getSourceColor = (source: string): string => {
    const colors = {
      installation: 'bg-blue-600',
      inspection: 'bg-green-600',
      maintenance: 'bg-orange-600',
      update: 'bg-purple-600',
      system: 'bg-cyan-600'
    };
    return colors[source as keyof typeof colors] || 'bg-gray-600';
  };

  const getSourceIcon = (source: string) => {
    const icons = {
      installation: <Building className="w-4 h-4" />,
      inspection: <CheckCircle className="w-4 h-4" />,
      maintenance: <Wrench className="w-4 h-4" />,
      update: <FileText className="w-4 h-4" />,
      system: <Zap className="w-4 h-4" />
    };
    return icons[source as keyof typeof icons] || <FileText className="w-4 h-4" />;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-engramiq-dark-blue border border-engramiq-green/20 rounded-lg shadow-engramiq-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        <div className="bg-gradient-green-blue p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-engramiq-dark-blue flex items-center gap-2">
              <History className="w-6 h-6" />
              {field} History
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-engramiq-dark-blue/20 rounded text-engramiq-dark-blue"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="mb-4 p-4 bg-engramiq-green/10 rounded-lg border border-engramiq-green/20">
            <div className="text-sm text-engramiq-bright-grey">Current Value</div>
            <div className="text-lg font-semibold text-engramiq-light-grey">
              {formatValue(currentValue)}
            </div>
          </div>

          <div className="space-y-4">
            {history
              .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
              .map((entry, index) => (
                <div key={entry.id} className="relative">
                  {index < history.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-8 bg-engramiq-bright-grey/30"></div>
                  )}
                  
                  <div className="flex gap-4 p-4 bg-surface-800/50 rounded-lg border border-engramiq-green/10">
                    <div className={`flex-shrink-0 w-12 h-12 ${getSourceColor(entry.source)} rounded-full flex items-center justify-center text-white`}>
                      {getSourceIcon(entry.source)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-engramiq-light-grey">
                          {formatValue(entry.value)}
                        </div>
                        <div className="text-xs text-engramiq-bright-grey flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {entry.timestamp.toLocaleDateString()} {entry.timestamp.toLocaleTimeString()}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-engramiq-bright-grey mb-2">
                        <div className="flex items-center gap-1">
                          <span className={`w-2 h-2 ${getSourceColor(entry.source)} rounded-full`}></span>
                          {entry.source.charAt(0).toUpperCase() + entry.source.slice(1)}
                        </div>
                        
                        {entry.updatedBy && (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {entry.updatedBy}
                          </div>
                        )}
                      </div>
                      
                      {entry.notes && (
                        <div className="text-sm text-engramiq-light-grey bg-engramiq-dark-blue/50 p-2 rounded">
                          {entry.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MetadataTab: React.FC = () => {
  const { state } = useAppContext();
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<MetadataHistoryEntry[]>([]);
  const [selectedCurrentValue, setSelectedCurrentValue] = useState<string | number | Date>('');
  const [enrichedMetadata, setEnrichedMetadata] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Load enriched metadata from generated files
  useEffect(() => {
    const loadEnrichedMetadata = async () => {
      if (state.selectedComponents.length === 0) {
        setEnrichedMetadata(null);
        return;
      }

      setLoading(true);
      try {
        // Try to load the enhanced network data
        const response = await fetch('/mock_data/solar_farm_comprehensive_data_enhanced_network_2025-09-02_14-07-18.json');
        if (response.ok) {
          const data = await response.json();
          // Find the selected component in the enhanced data
          const component = data.nodes.find((node: any) => node.id === state.selectedComponents[0]);
          if (component) {
            setEnrichedMetadata(component);
          }
        }
      } catch (error) {
        console.error('Error loading enriched metadata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnrichedMetadata();
  }, [state.selectedComponents]);

  const selectedMetadata = state.selectedComponents.length > 0 
    ? getComponentMetadata(state.selectedComponents[0])
    : null;

  const openTimeline = (field: string, history: MetadataHistoryEntry[], currentValue: string | number | Date) => {
    setSelectedField(field);
    setSelectedHistory(history);
    setSelectedCurrentValue(currentValue);
  };

  const closeTimeline = () => {
    setSelectedField(null);
    setSelectedHistory([]);
    setSelectedCurrentValue('');
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      operational: <CheckCircle className="w-5 h-5 text-green-500" />,
      maintenance: <Wrench className="w-5 h-5 text-orange-500" />,
      fault: <AlertTriangle className="w-5 h-5 text-accent-red" />,
      offline: <XCircle className="w-5 h-5 text-gray-500" />
    };
    return icons[status as keyof typeof icons] || <Pause className="w-5 h-5 text-gray-500" />;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (state.selectedComponents.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <Package className="w-16 h-16 text-engramiq-bright-grey mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">No Component Selected</h2>
          <p className="text-engramiq-bright-grey">
            Select a combiner box from the map or network graph to view its metadata and history.
          </p>
        </div>
      </div>
    );
  }

  if (!selectedMetadata) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <FileText className="w-16 h-16 text-engramiq-bright-grey mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">No Metadata Available</h2>
          <p className="text-engramiq-bright-grey">
            Metadata for component "{state.selectedComponents[0]}" is not available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-engramiq-dark-blue overflow-auto">
      <div className="p-6">
        {/* Component Header */}
        <div className="bg-gradient-green-blue p-6 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-engramiq-dark-blue mb-2">
                {selectedMetadata.label}
              </h1>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-engramiq-dark-blue" />
                  <span className="text-engramiq-dark-blue font-medium">
                    {selectedMetadata.type.charAt(0).toUpperCase() + selectedMetadata.type.slice(1)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(selectedMetadata.status.current)}
                  <span className="text-engramiq-dark-blue font-medium">
                    {selectedMetadata.status.current.charAt(0).toUpperCase() + selectedMetadata.status.current.slice(1)}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-engramiq-dark-blue/80">Installed</div>
              <div className="text-engramiq-dark-blue font-medium">
                {formatDate(selectedMetadata.installationDate)}
              </div>
            </div>
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Power Rating */}
          <div 
            className="bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20 hover:border-engramiq-green/40 cursor-pointer transition-all group"
            onClick={() => openTimeline('Power Rating', selectedMetadata.powerRating.history, `${selectedMetadata.powerRating.current} ${selectedMetadata.powerRating.unit}`)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-engramiq-green" />
                <h3 className="text-lg font-semibold text-engramiq-light-grey">Power Rating</h3>
              </div>
              <div className="flex items-center gap-2 text-engramiq-bright-grey group-hover:text-engramiq-green transition-colors">
                <History className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-engramiq-green mb-2">
              {selectedMetadata.powerRating.current} {selectedMetadata.powerRating.unit}
            </div>
            <div className="text-sm text-engramiq-bright-grey">
              {selectedMetadata.powerRating.history.length} history entries
            </div>
          </div>

          {/* Module Count */}
          <div 
            className="bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20 hover:border-engramiq-green/40 cursor-pointer transition-all group"
            onClick={() => openTimeline('Module Count', selectedMetadata.moduleCount.history, selectedMetadata.moduleCount.current)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Hash className="w-6 h-6 text-engramiq-green" />
                <h3 className="text-lg font-semibold text-engramiq-light-grey">Module Count</h3>
              </div>
              <div className="flex items-center gap-2 text-engramiq-bright-grey group-hover:text-engramiq-green transition-colors">
                <History className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-engramiq-green mb-2">
              {selectedMetadata.moduleCount.current}
            </div>
            <div className="text-sm text-engramiq-bright-grey">
              {selectedMetadata.moduleCount.history.length} history entries
            </div>
          </div>

          {/* Manufacturer */}
          <div 
            className="bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20 hover:border-engramiq-green/40 cursor-pointer transition-all group"
            onClick={() => openTimeline('Manufacturer', selectedMetadata.manufacturer.history, selectedMetadata.manufacturer.current)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Building className="w-6 h-6 text-engramiq-green" />
                <h3 className="text-lg font-semibold text-engramiq-light-grey">Manufacturer</h3>
              </div>
              <div className="flex items-center gap-2 text-engramiq-bright-grey group-hover:text-engramiq-green transition-colors">
                <History className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-engramiq-green mb-2">
              {selectedMetadata.manufacturer.current}
            </div>
            <div className="text-sm text-engramiq-bright-grey">
              {selectedMetadata.manufacturer.history.length} history entries
            </div>
          </div>

          {/* Model */}
          <div 
            className="bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20 hover:border-engramiq-green/40 cursor-pointer transition-all group"
            onClick={() => openTimeline('Model', selectedMetadata.model.history, selectedMetadata.model.current)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-engramiq-green" />
                <h3 className="text-lg font-semibold text-engramiq-light-grey">Model</h3>
              </div>
              <div className="flex items-center gap-2 text-engramiq-bright-grey group-hover:text-engramiq-green transition-colors">
                <History className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-engramiq-green mb-2">
              {selectedMetadata.model.current}
            </div>
            <div className="text-sm text-engramiq-bright-grey">
              {selectedMetadata.model.history.length} history entries
            </div>
          </div>

          {/* Serial Number */}
          <div 
            className="bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20 hover:border-engramiq-green/40 cursor-pointer transition-all group"
            onClick={() => openTimeline('Serial Number', selectedMetadata.serialNumber.history, selectedMetadata.serialNumber.current)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Hash className="w-6 h-6 text-engramiq-green" />
                <h3 className="text-lg font-semibold text-engramiq-light-grey">Serial Number</h3>
              </div>
              <div className="flex items-center gap-2 text-engramiq-bright-grey group-hover:text-engramiq-green transition-colors">
                <History className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-2xl font-bold text-engramiq-green mb-2 font-mono">
              {selectedMetadata.serialNumber.current}
            </div>
            <div className="text-sm text-engramiq-bright-grey">
              {selectedMetadata.serialNumber.history.length} history entries
            </div>
          </div>

          {/* Inspection Date */}
          <div 
            className="bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20 hover:border-engramiq-green/40 cursor-pointer transition-all group"
            onClick={() => openTimeline('Inspection Date', selectedMetadata.inspectionDate.history, selectedMetadata.inspectionDate.current)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-engramiq-green" />
                <h3 className="text-lg font-semibold text-engramiq-light-grey">Last Inspection</h3>
              </div>
              <div className="flex items-center gap-2 text-engramiq-bright-grey group-hover:text-engramiq-green transition-colors">
                <History className="w-4 h-4" />
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
            <div className="text-lg font-bold text-engramiq-green mb-2">
              {formatDate(selectedMetadata.inspectionDate.current)}
            </div>
            <div className="text-sm text-engramiq-bright-grey">
              {selectedMetadata.inspectionDate.history.length} inspection records
            </div>
          </div>
        </div>

        {/* Enriched Metadata Section */}
        {enrichedMetadata && enrichedMetadata.metadata && (
          <div className="mt-6 bg-gradient-green-blue/20 p-6 rounded-lg border border-engramiq-green/30">
            <h3 className="text-lg font-semibold text-engramiq-light-grey mb-4 flex items-center gap-3">
              <Package className="w-6 h-6 text-engramiq-green" />
              Enhanced Component Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {enrichedMetadata.metadata.manufacturer && (
                <div>
                  <div className="text-sm text-engramiq-bright-grey">Manufacturer</div>
                  <div className="text-lg font-medium text-engramiq-light-grey">
                    {enrichedMetadata.metadata.manufacturer}
                  </div>
                </div>
              )}
              {enrichedMetadata.metadata.model && (
                <div>
                  <div className="text-sm text-engramiq-bright-grey">Model</div>
                  <div className="text-lg font-medium text-engramiq-light-grey">
                    {enrichedMetadata.metadata.model}
                  </div>
                </div>
              )}
              {enrichedMetadata.metadata.serial_number && (
                <div>
                  <div className="text-sm text-engramiq-bright-grey">Serial Number</div>
                  <div className="text-lg font-mono text-engramiq-light-grey">
                    {enrichedMetadata.metadata.serial_number}
                  </div>
                </div>
              )}
              {enrichedMetadata.metadata.installation_date && (
                <div>
                  <div className="text-sm text-engramiq-bright-grey">Installation Date</div>
                  <div className="text-lg font-medium text-engramiq-light-grey">
                    {new Date(enrichedMetadata.metadata.installation_date).toLocaleDateString()}
                  </div>
                </div>
              )}
              {enrichedMetadata.metadata.warranty_expiry && (
                <div>
                  <div className="text-sm text-engramiq-bright-grey">Warranty Expiry</div>
                  <div className="text-lg font-medium text-engramiq-light-grey">
                    {new Date(enrichedMetadata.metadata.warranty_expiry).toLocaleDateString()}
                  </div>
                </div>
              )}
              {enrichedMetadata.metadata.enclosure && (
                <div>
                  <div className="text-sm text-engramiq-bright-grey">Enclosure Rating</div>
                  <div className="text-lg font-medium text-engramiq-light-grey">
                    {enrichedMetadata.metadata.enclosure}
                  </div>
                </div>
              )}
              
              {/* Type-specific fields */}
              {enrichedMetadata.node_type === 'inverter' && (
                <>
                  {enrichedMetadata.metadata.max_ac_power_kwac && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Max AC Power</div>
                      <div className="text-lg font-medium text-engramiq-green">
                        {enrichedMetadata.metadata.max_ac_power_kwac} kW
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.cec_efficiency_percent && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">CEC Efficiency</div>
                      <div className="text-lg font-medium text-engramiq-green">
                        {enrichedMetadata.metadata.cec_efficiency_percent}%
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.num_mppt && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">MPPT Trackers</div>
                      <div className="text-lg font-medium text-engramiq-light-grey">
                        {enrichedMetadata.metadata.num_mppt}
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.dc_ac_ratio_calculated && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">DC/AC Ratio</div>
                      <div className="text-lg font-medium text-engramiq-light-grey">
                        {enrichedMetadata.metadata.dc_ac_ratio_calculated}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {enrichedMetadata.node_type === 'combiner_box' && (
                <>
                  {enrichedMetadata.metadata.input_circuits && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Input Circuits</div>
                      <div className="text-lg font-medium text-engramiq-green">
                        {enrichedMetadata.metadata.input_circuits}
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.total_dc_power_kw && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Total DC Power</div>
                      <div className="text-lg font-medium text-engramiq-green">
                        {enrichedMetadata.metadata.total_dc_power_kw} kW
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.dc_voltage_rating_vdc && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">DC Voltage Rating</div>
                      <div className="text-lg font-medium text-engramiq-light-grey">
                        {enrichedMetadata.metadata.dc_voltage_rating_vdc} VDC
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {enrichedMetadata.node_type === 'transformer' && (
                <>
                  {enrichedMetadata.metadata.rating_kva && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Rating</div>
                      <div className="text-lg font-medium text-engramiq-green">
                        {enrichedMetadata.metadata.rating_kva} kVA
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.primary_voltage_vac && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Primary Voltage</div>
                      <div className="text-lg font-medium text-engramiq-light-grey">
                        {enrichedMetadata.metadata.primary_voltage_vac}
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.secondary_voltage_vac && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Secondary Voltage</div>
                      <div className="text-lg font-medium text-engramiq-light-grey">
                        {enrichedMetadata.metadata.secondary_voltage_vac}
                      </div>
                    </div>
                  )}
                  {enrichedMetadata.metadata.impedance_percent && (
                    <div>
                      <div className="text-sm text-engramiq-bright-grey">Impedance</div>
                      <div className="text-lg font-medium text-engramiq-light-grey">
                        {enrichedMetadata.metadata.impedance_percent}%
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {enrichedMetadata.metadata.description && (
              <div className="mt-4 p-4 bg-surface-800/50 rounded">
                <div className="text-sm text-engramiq-bright-grey mb-2">Description</div>
                <div className="text-sm text-engramiq-light-grey">
                  {enrichedMetadata.metadata.description}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Location Info */}
        {selectedMetadata.location && (
          <div className="mt-6 bg-surface-800/50 p-6 rounded-lg border border-engramiq-green/20">
            <h3 className="text-lg font-semibold text-engramiq-light-grey mb-4 flex items-center gap-3">
              <FileText className="w-6 h-6 text-engramiq-green" />
              Location
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-engramiq-bright-grey">Latitude</div>
                <div className="text-lg font-mono text-engramiq-light-grey">
                  {selectedMetadata.location.lat.toFixed(6)}
                </div>
              </div>
              <div>
                <div className="text-sm text-engramiq-bright-grey">Longitude</div>
                <div className="text-lg font-mono text-engramiq-light-grey">
                  {selectedMetadata.location.lng.toFixed(6)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Timeline Modal */}
      <TimelineModal
        isOpen={selectedField !== null}
        onClose={closeTimeline}
        field={selectedField || ''}
        history={selectedHistory}
        currentValue={selectedCurrentValue}
      />
    </div>
  );
};

export default MetadataTab;